import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api } from "../lib/api";
import { useSEO } from "../lib/seo";
import { RatingStars, SoftwareLogo } from "../components/SoftwareCard";
import { Trophy, ArrowRight } from "lucide-react";

export default function BestOf() {
  const [software, setSoftware] = useState([]);

  useSEO({
    title: "Best Pool Service Software 2026 (Ranked & Compared)",
    description: "Our ranked list of the best pool service software in 2026: Skimmer, Pool Brain, Jobber and more, with real pricing and cited user ratings.",
    jsonLd: {
      "@context": "https://schema.org",
      "@type": "ItemList",
      name: "Best Pool Service Software 2026",
      itemListElement: software.map((s, i) => ({ "@type": "ListItem", position: i + 1, name: s.name, url: `${window.location.origin}/software/${s.slug}` })),
    },
  });

  useEffect(() => {
    api.get("/software").then(({ data }) => setSoftware(data));
  }, []);

  return (
    <div className="max-w-5xl mx-auto px-4 sm:px-6 py-16" data-testid="bestof-page">
      <p className="uppercase tracking-[0.2em] text-xs font-semibold text-sky-600 mb-2 flex items-center gap-2">
        <Trophy className="w-4 h-4" aria-hidden="true" /> Ranked list · 2026
      </p>
      <h1 className="font-heading text-4xl sm:text-5xl font-black tracking-tight text-slate-900">Best Pool Service Software 2026</h1>
      <p className="mt-4 text-slate-600 max-w-2xl leading-relaxed">
        We researched every major platform on routes, chemistry, billing, and mobile usability — comparing official pricing and verified user ratings. Here's how they rank, and who each tool is actually right for.
      </p>

      <div className="mt-14 space-y-8">
        {software.map((s, i) => (
          <div key={s.slug} data-testid={`bestof-item-${s.slug}`} className="bg-white rounded-2xl border border-border p-6 md:p-8 hover:border-sky-300 hover:shadow-lg hover:shadow-sky-100 transition-shadow duration-200">
            <div className="flex flex-col md:flex-row md:items-start gap-5">
              <div className="flex items-center gap-4 shrink-0">
                <span className={`w-12 h-12 rounded-full flex items-center justify-center font-heading font-black text-lg ${i === 0 ? "bg-amber-400 text-slate-900" : "bg-slate-900 text-white"}`}>
                  #{i + 1}
                </span>
                <SoftwareLogo software={s} size="w-14 h-14" />
              </div>
              <div className="flex-1 min-w-0">
                <div className="flex flex-wrap items-center gap-3">
                  <h2 className="font-heading font-bold text-2xl text-slate-900">{s.name}</h2>
                  <RatingStars rating={s.rating} reviewTotal={s.review_total} />
                  {i === 0 && <span className="text-xs px-3 py-1 rounded-full bg-amber-100 text-amber-800 font-bold uppercase tracking-wide">Editor's Choice</span>}
                </div>
                <p className="text-slate-600 mt-2 leading-relaxed">{s.tagline}</p>
                <p className="text-sm text-slate-500 mt-3 leading-relaxed"><span className="font-semibold text-slate-700">Best for:</span> {s.best_for}</p>
                <div className="mt-4 flex flex-wrap items-center gap-3">
                  <span className="text-sm font-bold text-slate-900">{s.pricing_start}</span>
                  <Link to={`/software/${s.slug}`} data-testid={`bestof-read-review-${s.slug}`} className="inline-flex items-center gap-1.5 px-4 py-2 rounded-full bg-sky-500 text-white text-sm font-semibold hover:bg-sky-600 active:scale-95 transition-colors duration-200">
                    Read full review <ArrowRight className="w-4 h-4" aria-hidden="true" />
                  </Link>
                </div>
              </div>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}
